<template>
  <!-- Figma auto-connect: hero status + spinner; rail = video icon only -->
  <v-container fluid class="pa-0 fill-height bg-backgroundBase vc-incoming-root vc-tv-canvas-bg">
    <PatientBackground />

    <div class="vc-incoming-hero">
      <h1 class="vc-incoming-hero__title text-wrap">{{ primaryLine }}</h1>
      <p class="vc-incoming-hero__purpose text-wrap">{{ secondaryLine }}</p>
      <div class="vc-incoming-hero__connecting">
        <v-progress-circular color="primary" indeterminate size="48" width="4" />
      </div>
    </div>

    <v-sheet
      class="vc-incoming-panel vc-connecting-panel d-flex flex-column bg-white"
      elevation="24"
      height="100%"
      rounded="0"
    >
      <div class="mb-16">
        <v-icon class="vc-rail__video-icon" color="primary">mdi-video-plus-outline</v-icon>
      </div>
      <div class="flex-grow-1" aria-hidden="true" />
    </v-sheet>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { apiClient } from '../services/apiClient'
import { signalingService } from '../services/signalingService'
import PatientBackground from '../components/PatientBackground.vue'
import { breakthroughPrimaryLine, breakthroughSecondaryLine } from '@virtual-care/shared'
import type { SessionCredentials } from '@virtual-care/shared'

const router = useRouter()
const callStore = useCallStore()

const primaryLine = breakthroughPrimaryLine()
const secondaryLine = breakthroughSecondaryLine()

onMounted(async () => {
  const sessionId = callStore.sessionId
  const roomId = apiClient.getRoomId()
  
  if (!sessionId) {
    router.replace('/')
    return
  }

  try {
    // Join session to get provider and credentials (same as normal call flow)
    const joinResponse = await apiClient.joinSession(sessionId, 'patient')

    console.log('[BreakthroughCallView] Joined session', {
      provider: joinResponse.session?.provider,
      hasCredentials: !!joinResponse.session?.credentials
    })

    const provider = joinResponse.session?.provider
    if (!provider) {
      throw new Error('No video provider returned from backend')
    }
    
    const credentials = joinResponse.session?.credentials

    // Store provider and credentials in callStore (critical for correct video client!)
    callStore.acceptCall(
      provider as never,
      credentials as SessionCredentials | undefined
    )

    // Notify nurse that patient has connected (sends call_accepted event)
    signalingService.notifyCallAccepted(sessionId, roomId)

    // Navigate to active call view
    router.replace(`/call/${sessionId}`)
  } catch (error) {
    console.error('[BreakthroughCallView] Failed to join session:', error)
    callStore.endCall()
    router.replace('/')
  }
})
</script>
